import React, { useState, useEffect, useCallback } from 'react';
import { FileText, CreditCard } from 'lucide-react';
import CheckoutSheet from './CheckoutSheet';
import { useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import SAR             from '../../../components/SAR';

import visaLogo        from '../../../assets/payment/visa-logo.svg';
import madaLogo        from '../../../assets/payment/mada-logo.svg';
import applePayLogo    from '../../../assets/payment/apple-pay-logo.svg';
import mastercardLogo  from '../../../assets/payment/mastercard-logo.svg';
import paymentCardIcon from '../../../assets/payment/payment-card-logo.svg';

const walletLogo = '/assets/images/sysIcons/wallet.svg';
const whiteLogo  = '/assets/images/logo/white-logo.png';

const TX_LABELS = {
  topup:      { label: 'شحن رصيد',    color: 'text-green-600',  bg: 'bg-green-50',  icon: '⬆' },
  payment:    { label: 'دفع طلب',     color: 'text-red-500',    bg: 'bg-red-50',    icon: '⬇' },
  refund:     { label: 'استرداد',     color: 'text-blue-600',   bg: 'bg-blue-50',   icon: '↩' },
  adjustment: { label: 'تعديل يدوي', color: 'text-amber-600',  bg: 'bg-amber-50',  icon: '⚙' },
};

const REQ_STATUS = {
  pending:    { label: 'قيد المراجعة', color: 'text-amber-700',  bg: 'bg-amber-50',  border: 'border-amber-200' },
  approved:   { label: 'تمت الموافقة', color: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200' },
  rejected:   { label: 'مرفوض',        color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200'   },
  processing: { label: 'جاري المعالجة',color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200'  },
  failed:     { label: 'فشل',          color: 'text-gray-600',   bg: 'bg-gray-50',   border: 'border-gray-200'  },
};

const BANK_DETAILS = {
  bankName:    'البنك الأهلي السعودي',
  accountName: 'شركة وترلي للتقنية',
  iban:        'SA00 0000 0000 0000 0000 0000',
  note:        'يرجى كتابة رقم هاتفك في ملاحظات التحويل',
};

export default function WalletPage() {
  const { apiCall }         = useCustomerAuth();
  const toast               = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab]               = useState('history');   // history | requests | topup
  const [balance, setBalance]       = useState(0);
  const [pendingCount, setPending]  = useState(0);
  const [loading, setLoading]       = useState(true);

  // Payment callback result
  const [payResult, setPayResult]   = useState(null); // null | { type: 'success'|'failed'|'cancelled', amount, balance }

  // Transaction history
  const [txs, setTxs]               = useState([]);
  const [txPage, setTxPage]         = useState(1);
  const [txPages, setTxPages]       = useState(1);
  const [txTotal, setTxTotal]       = useState(0);
  const [txLoading, setTxLoading]   = useState(false);

  // Top-up requests
  const [reqs, setReqs]             = useState([]);
  const [reqPage, setReqPage]       = useState(1);
  const [reqPages, setReqPages]     = useState(1);
  const [reqLoading, setReqLoading] = useState(false);

  // Top-up form
  const [topupMethod, setTopupMethod] = useState('card');  // 'card' | 'bank'
  const [form, setForm]             = useState({ amount: '', bank_ref: '', bank_name: '', customer_notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [cardAmount, setCardAmount]     = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const fmt = (n) => Number(n ?? 0).toFixed(2);
  const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('ar-SA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ── Handle Telr redirect return (topup_result in URL from TopupCallbackPage) ──
  useEffect(() => {
    const result  = searchParams.get('topup_result');
    const balance = searchParams.get('topup_balance');
    if (!result) return;
    setSearchParams({}, { replace: true });
    if (result === 'success') {
      const newBal = balance ? parseFloat(balance) : null;
      setPayResult({ type: 'success', balance: newBal });
      if (newBal !== null) setBalance(newBal);
      setTimeout(() => loadWallet(), 0);
    } else if (result === 'cancelled') {
      setPayResult({ type: 'cancelled' });
    } else {
      setPayResult({ type: 'failed' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle Telr return params on mount (legacy) ──
  useEffect(() => {
    const payment = searchParams.get('payment');
    const reqid   = searchParams.get('reqid');
    if (!payment) return;

    // Clear URL params immediately
    setSearchParams({}, { replace: true });

    if (payment === 'can') {
      setPayResult({ type: 'cancelled' });
      return;
    }
    if (payment === 'decl') {
      setPayResult({ type: 'failed' });
      return;
    }
    if (payment === 'auth' && reqid) {
      setPayResult({ type: 'verifying' });
      // Verify with backend
      apiCall(`/customer/payment/topup/verify?reqid=${encodeURIComponent(reqid)}`, { method: 'POST' })
        .then(res => {
          if (res.paid) {
            setPayResult({ type: 'success', amount: res.amount, balance: res.new_balance });
            setBalance(res.new_balance);
            setTab('history');
          } else {
            setPayResult({ type: 'failed' });
          }
        })
        .catch(() => setPayResult({ type: 'failed' }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load wallet overview ──
  const loadWallet = useCallback(async () => {
    try {
      const res = await apiCall('/customer/wallet');
      setBalance(res.balance);
      setTxs(res.transactions || []);
      setPending(res.pending_topups || 0);
    } catch { toast.error('تعذر تحميل المحفظة'); }
    finally { setLoading(false); }
  }, [apiCall, toast]);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // ── Load full transaction history ──
  const loadTxs = useCallback(async (p = 1) => {
    setTxLoading(true);
    try {
      const r = await apiCall(`/customer/wallet/transactions?page=${p}&limit=20`);
      setTxs(r.transactions); setTxPage(p); setTxPages(r.pages); setTxTotal(r.total);
    } catch { /* silent */ } finally { setTxLoading(false); }
  }, [apiCall]);

  // ── Load top-up requests ──
  const loadReqs = useCallback(async (p = 1) => {
    setReqLoading(true);
    try {
      const r = await apiCall(`/customer/wallet/topup-requests?page=${p}&limit=20`);
      setReqs(r.requests); setReqPage(p); setReqPages(r.pages);
    } catch { /* silent */ } finally { setReqLoading(false); }
  }, [apiCall]);

  const handleTabChange = (t) => {
    setTab(t);
    if (t === 'history' && txs.length === 0) loadTxs(1);
    if (t === 'requests') loadReqs(1);
  };

  // ── Submit card payment via Telr ──
  const submitCardTopup = async (e) => {
    e.preventDefault();
    if (!cardAmount || Number(cardAmount) <= 0) { toast.error('أدخل مبلغاً صحيحاً'); return; }
    setSubmitting(true);
    try {
      const res = await apiCall('/customer/payment/topup/init', {
        method: 'POST',
        body: JSON.stringify({ amount: Number(cardAmount) }),
      });
      // Redirect to Telr payment page
      window.location.href = res.payment_url;
    } catch (err) {
      toast.error(err.message || 'تعذر الاتصال ببوابة الدفع');
      setSubmitting(false);
    }
  };

  // ── Submit bank transfer request ──
  const submitTopup = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error('أدخل مبلغاً صحيحاً'); return; }
    if (!form.bank_ref.trim()) { toast.error('أدخل رقم الحوالة المرجعي'); return; }
    setSubmitting(true);
    try {
      await apiCall('/customer/wallet/topup-request', {
        method: 'POST',
        body: JSON.stringify({
          amount:         Number(form.amount),
          bank_ref:       form.bank_ref.trim(),
          bank_name:      form.bank_name.trim() || undefined,
          customer_notes: form.customer_notes.trim() || undefined,
        }),
      });
      toast.success('تم إرسال طلب الشحن بنجاح — سيتم مراجعته خلال 24 ساعة');
      setForm({ amount: '', bank_ref: '', bank_name: '', customer_notes: '' });
      setTab('requests');
      loadReqs(1);
      loadWallet();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const S = {
    cardBg:     'var(--wt-surface)',
    cardBorder: 'var(--wt-border)',
    text:       'var(--wt-text-primary)',
    muted:      'var(--wt-text-muted)',
    secondary:  'var(--wt-text-secondary)',
    inputBg:    'var(--wt-input-bg, rgba(0,0,0,0.06))',
    inputBorder:'var(--wt-border)',
    divider:    'var(--wt-divider)',
  };

  const inputStyle = (focused) => ({
    width:'100%', padding:'0.72rem 0.9rem', borderRadius:'0.75rem', outline:'none',
    fontSize:'0.875rem', fontWeight:600, fontFamily:"'Cairo',sans-serif",
    background:'var(--wt-surface)', color: S.text,
    border:`1.5px solid ${focused ? 'rgba(10,186,181,0.7)' : S.inputBorder}`,
    boxShadow: focused ? '0 0 0 3px rgba(10,186,181,0.12)' : 'none',
    transition:'border 0.2s,box-shadow 0.2s', boxSizing:'border-box',
  });

  if (loading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'5rem 0', gap:'0.75rem' }}>
        <div style={{ width:36, height:36, borderRadius:'50%', border:'3px solid rgba(10,186,181,0.2)', borderTopColor:'#0ABAB5', animation:'wt-spin 0.7s linear infinite' }}/>
        <p style={{ color:'var(--wt-text-muted)', fontSize:'0.8rem', fontFamily:"'Cairo',sans-serif" }}>جاري تحميل المحفظة...</p>
        <style>{`@keyframes wt-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div dir="rtl" style={{ display:'flex', flexDirection:'column', gap:'1rem', fontFamily:"'Cairo',sans-serif", paddingBottom:'1.5rem' }}>
      <style>{`
        @keyframes wt-spin    { to{transform:rotate(360deg)} }
        @keyframes wt-bal-in  { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes wt-card-in { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .wt-wallet-input { outline:none; width:100%; box-sizing:border-box; }
        .wt-wallet-input::placeholder { opacity:0.4; }
        .wt-tx-row:hover { background:rgba(10,186,181,0.04); }
      `}</style>

      {/* ══ HERO CARD ══ */}
      <div style={{
        borderRadius:'1.375rem', overflow:'hidden', position:'relative',
        background:'linear-gradient(135deg,#062040 0%,#083060 45%,#0a3d75 100%)',
        boxShadow:'0 16px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(10,186,181,0.15)',
        animation:'wt-bal-in 0.4s ease',
      }}>
        {/* Ambient blobs */}
        <div style={{ position:'absolute', width:220, height:220, borderRadius:'50%', top:'-60px', left:'-50px', background:'radial-gradient(circle,rgba(10,186,181,0.18) 0%,transparent 70%)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', width:160, height:160, borderRadius:'50%', bottom:'-40px', right:'-30px', background:'radial-gradient(circle,rgba(56,189,248,0.12) 0%,transparent 70%)', pointerEvents:'none' }}/>
        {/* Watermark logo */}
        <img src={whiteLogo} alt="" aria-hidden="true" style={{
          position:'absolute', right:'100%', top:'50%', transform:'translateX(35%) translateY(-50%)',
          width:'auto', height:'140%', objectFit:'contain', pointerEvents:'none', userSelect:'none',
          opacity:0.08,
          filter:'invert(70%) sepia(80%) saturate(400%) hue-rotate(140deg) brightness(1.3)',
        }}/>
        {/* Top accent */}
        <div style={{ height:3, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.7),transparent)' }}/>

        <div style={{ padding:'1.25rem 1.25rem 1rem', position:'relative' }}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.1rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
              <div style={{
                width:48, height:48, borderRadius:'0.875rem',
                background:'rgba(10,186,181,0.15)', border:'1px solid rgba(10,186,181,0.3)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 0 16px rgba(10,186,181,0.2)',
              }}>
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 12a1 1 0 100 2 1 1 0 000-2z" fill="#0ABAB5" stroke="none"/><path d="M2 9h20"/></svg>
              </div>
              <div>
                <div style={{ fontSize:'0.62rem', color:'rgba(186,230,253,0.5)', fontWeight:700, letterSpacing:'0.08em' }}>WTRLY</div>
                <div style={{ fontSize:'0.88rem', color:'rgba(240,249,255,0.95)', fontWeight:800, lineHeight:1.1 }}>محفظتي</div>
              </div>
            </div>
            <button onClick={loadWallet} style={{
              width:34, height:34, borderRadius:'50%', border:'none', cursor:'pointer',
              background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center',
              color:'rgba(186,230,253,0.7)',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
            </button>
          </div>

          {/* Balance */}
          <div style={{ marginBottom:'1.1rem' }}>
            <div style={{ fontSize:'0.68rem', color:'rgba(186,230,253,0.45)', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'0.35rem' }}>الرصيد المتاح</div>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <span style={{ fontSize:'2.6rem', fontWeight:900, color:'#fff', lineHeight:1, letterSpacing:'-0.03em' }}>{fmt(balance)}</span>
              <SAR style={{ height:'1.8rem', width:'auto', color:'rgba(10,186,181,0.7)' }}/>
            </div>
          </div>

          {/* Pending notice */}
          {pendingCount > 0 && (
            <div style={{
              display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.875rem',
              background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.25)',
              borderRadius:'0.75rem', padding:'0.5rem 0.75rem',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
              </svg>
              <span style={{ fontSize:'0.72rem', color:'rgba(253,230,138,0.9)', fontWeight:700 }}>
                {pendingCount} طلب شحن قيد المراجعة
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem', paddingTop:'0.875rem', borderTop:'1px solid rgba(255,255,255,0.08)' }}>
            {[
              { key:'topup',    label:'شحن',        icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 4v16M4 12h16"/></svg> },
              { key:'history',  label:'المعاملات',  icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg> },
              { key:'requests', label:'الطلبات',    icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg> },
            ].map(a => {
              const isAct = tab === a.key;
              return (
                <button key={a.key} onClick={() => handleTabChange(a.key)} style={{
                  display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem',
                  padding:'0.6rem 0.4rem', borderRadius:'0.875rem', border:'none', cursor:'pointer',
                  background: isAct ? 'rgba(10,186,181,0.22)' : 'rgba(255,255,255,0.07)',
                  color: isAct ? '#0ABAB5' : 'rgba(186,230,253,0.7)',
                  boxShadow: isAct ? '0 0 14px rgba(10,186,181,0.25), inset 0 0 0 1px rgba(10,186,181,0.35)' : 'none',
                  transition:'all 0.18s',
                }}>
                  {a.icon}
                  <span style={{ fontSize:'0.65rem', fontWeight:800 }}>{a.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══ PAYMENT RESULT BANNER ══ */}
      {payResult && (() => {
        const cfg = payResult.type === 'success'   ? { color:'#10b981', bg:'rgba(16,185,129,0.1)', border:'rgba(16,185,129,0.3)' }
                  : payResult.type === 'cancelled' ? { color:'#f59e0b', bg:'rgba(245,158,11,0.1)',  border:'rgba(245,158,11,0.3)' }
                  : payResult.type === 'verifying' ? { color:'#0ABAB5', bg:'rgba(10,186,181,0.1)', border:'rgba(10,186,181,0.3)' }
                  :                                  { color:'#f43f5e', bg:'rgba(244,63,94,0.1)',   border:'rgba(244,63,94,0.3)' };
        return (
          <div style={{
            display:'flex', alignItems:'flex-start', gap:'0.75rem', padding:'0.875rem',
            borderRadius:'0.875rem', background:cfg.bg, border:`1px solid ${cfg.border}`,
            backdropFilter:'blur(12px)',
          }}>
            <div style={{ width:34, height:34, borderRadius:'0.625rem', flexShrink:0, background:`rgba(${cfg.color==='#10b981'?'16,185,129':cfg.color==='#f59e0b'?'245,158,11':cfg.color==='#0ABAB5'?'10,186,181':'244,63,94'},0.15)`, display:'flex', alignItems:'center', justifyContent:'center', color:cfg.color }}>
              {payResult.type === 'verifying'
                ? <span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(10,186,181,0.3)', borderTopColor:'#0ABAB5', animation:'wt-spin 0.7s linear infinite', display:'inline-block' }}/>
                : payResult.type === 'success' ? '✓' : payResult.type === 'cancelled' ? '!' : '✕'
              }
            </div>
            <div style={{ flex:1 }}>
              {payResult.type === 'success'   && <p style={{ color:'#10b981', fontWeight:800, fontSize:'0.82rem', margin:'0 0 0.2rem' }}>تم شحن المحفظة بنجاح!</p>}
              {payResult.type === 'failed'    && <p style={{ color:'#f43f5e', fontWeight:800, fontSize:'0.82rem', margin:'0 0 0.2rem' }}>فشلت عملية الدفع</p>}
              {payResult.type === 'cancelled' && <p style={{ color:'#f59e0b', fontWeight:800, fontSize:'0.82rem', margin:'0 0 0.2rem' }}>تم إلغاء العملية</p>}
              {payResult.type === 'verifying' && <p style={{ color:'#0ABAB5', fontWeight:800, fontSize:'0.82rem', margin:0 }}>جاري التحقق من الدفع...</p>}
              {payResult.type === 'success' && <p style={{ color:'rgba(16,185,129,0.8)', fontSize:'0.7rem', margin:0 }}>تمت إضافة {fmt(payResult.amount)} ر.س — رصيدك: {fmt(payResult.balance)} ر.س</p>}
              {payResult.type === 'failed'    && <p style={{ color:'rgba(244,63,94,0.75)', fontSize:'0.7rem', margin:0 }}>لم يتم خصم أي مبلغ — يمكنك المحاولة مجدداً</p>}
              {payResult.type === 'cancelled' && <p style={{ color:'rgba(245,158,11,0.75)', fontSize:'0.7rem', margin:0 }}>لم يتم خصم أي مبلغ</p>}
            </div>
            <button onClick={() => setPayResult(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--wt-text-muted)', fontSize:'1.2rem', lineHeight:1, padding:'0 0.2rem' }}>×</button>
          </div>
        );
      })()}

      {/* ══ TAB PANEL ══ */}
      <div style={{ borderRadius:'1rem', overflow:'hidden', background:'var(--wt-surface)', border:'1px solid var(--wt-border)' }}>

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--wt-border)' }}>
          {[
            { key:'history',  label:'المعاملات',   badge: txTotal },
            { key:'requests', label:'طلبات الشحن', badge: pendingCount },
            { key:'topup',    label:'شحن جديد',    badge: 0 },
          ].map(t => {
            const isAct = tab === t.key;
            return (
              <button key={t.key} onClick={() => handleTabChange(t.key)} style={{
                flex:1, padding:'0.875rem 0.5rem', border:'none', cursor:'pointer',
                background:'transparent', position:'relative',
                fontSize:'0.75rem', fontWeight:700, fontFamily:"'Cairo',sans-serif",
                color: isAct ? '#0ABAB5' : 'var(--wt-text-muted)',
                display:'flex', alignItems:'center', justifyContent:'center', gap:'0.35rem',
                transition:'color 0.18s',
              }}>
                {t.label}
                {t.badge > 0 && (
                  <span style={{
                    fontSize:'0.6rem', fontWeight:800, borderRadius:999,
                    padding:'0.05rem 0.4rem', lineHeight:1.6,
                    background: isAct ? 'rgba(10,186,181,0.18)' : 'var(--wt-border)',
                    color: isAct ? '#0ABAB5' : 'var(--wt-text-muted)',
                    border: isAct ? '1px solid rgba(10,186,181,0.3)' : 'none',
                  }}>{t.badge > 9 ? '9+' : t.badge}</span>
                )}
                {isAct && <div style={{ position:'absolute', bottom:0, right:0, left:0, height:2, background:'linear-gradient(90deg,transparent,#0ABAB5,transparent)', borderRadius:'2px 2px 0 0' }}/>}
              </button>
            );
          })}
        </div>

        {/* ── History ── */}
        {tab === 'history' && (
          <div>
            {txLoading ? (
              <div style={{ padding:'3rem 0', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.75rem' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', border:'3px solid rgba(10,186,181,0.2)', borderTopColor:'#0ABAB5', animation:'wt-spin 0.7s linear infinite' }}/>
                <span style={{ color:'var(--wt-text-muted)', fontSize:'0.75rem' }}>جاري التحميل...</span>
              </div>
            ) : txs.length === 0 ? (
              <div style={{ padding:'3rem 1.5rem', textAlign:'center' }}>
                <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.15)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 0.875rem' }}>
                  <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.75}}><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 12a1 1 0 100 2 1 1 0 000-2z" fill="#0ABAB5" stroke="none"/><path d="M2 9h20"/></svg>
                </div>
                <p style={{ color:'var(--wt-text-primary)', fontWeight:700, margin:'0 0 0.3rem', fontSize:'0.875rem' }}>لا توجد معاملات بعد</p>
                <p style={{ color:'var(--wt-text-muted)', fontSize:'0.75rem', margin:0 }}>ستظهر هنا جميع عمليات الشحن والدفع</p>
              </div>
            ) : (
              <div>
                {txs.map((tx, i) => {
                  const TX_CFG = {
                    topup:      { label:'شحن رصيد',    color:'#10b981', bg:'rgba(16,185,129,0.1)',  icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg> },
                    payment:    { label:'دفع طلب',     color:'#f43f5e', bg:'rgba(244,63,94,0.1)',   icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg> },
                    refund:     { label:'استرداد',     color:'#0ABAB5', bg:'rgba(10,186,181,0.1)',  icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="2.5" strokeLinecap="round"><path d="M3 10h11a4 4 0 010 8H4"/><path d="M7 6l-4 4 4 4"/></svg> },
                    adjustment: { label:'تعديل يدوي', color:'#f59e0b', bg:'rgba(245,158,11,0.1)',  icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 115 19"/></svg> },
                  };
                  const meta = TX_CFG[tx.type] || { label:tx.type, color:'var(--wt-text-muted)', bg:'var(--wt-border)', icon:'•' };
                  const isCredit = tx.amount > 0;
                  return (
                    <div key={tx.id} className="wt-tx-row" style={{
                      display:'flex', alignItems:'center', gap:'0.75rem',
                      padding:'0.875rem 1rem',
                      borderBottom: i < txs.length-1 ? '1px solid var(--wt-border)' : 'none',
                      transition:'background 0.15s', cursor:'default',
                      animation:`wt-card-in 0.3s ease ${i*0.03}s both`,
                    }}>
                      <div style={{ width:38, height:38, borderRadius:'0.75rem', flexShrink:0, background:meta.bg, border:`1px solid ${meta.color}22`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {meta.icon}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ color:'var(--wt-text-primary)', fontWeight:700, fontSize:'0.82rem', marginBottom:'0.12rem' }}>{meta.label}</div>
                        {tx.description && <div style={{ color:'var(--wt-text-muted)', fontSize:'0.68rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description}</div>}
                        <div style={{ color:'var(--wt-text-muted)', fontSize:'0.65rem', marginTop:'0.1rem', opacity:0.7 }}>{fmtDate(tx.created_at)}</div>
                      </div>
                      <div style={{ textAlign:'left', flexShrink:0 }}>
                        <div style={{ color: isCredit ? '#10b981' : '#f43f5e', fontWeight:800, fontSize:'0.88rem', display:'flex', alignItems:'center', gap:'0.2rem', direction:'ltr' }}>
                          {isCredit ? '+' : ''}<SAR style={{ height:'0.85em', width:'auto' }}/>{fmt(tx.amount)}
                        </div>
                        <div style={{ color:'var(--wt-text-muted)', fontSize:'0.62rem', marginTop:'0.12rem', display:'flex', alignItems:'center', gap:'0.15rem', direction:'ltr' }}>
                          <SAR style={{ height:'0.75em', width:'auto', opacity:0.6 }}/>{fmt(tx.balance_after)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {txPages > 1 && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 1rem', borderTop:'1px solid var(--wt-border)' }}>
                <button disabled={txPage<=1||txLoading} onClick={() => loadTxs(txPage-1)} style={{ fontSize:'0.75rem', fontWeight:700, color:'#0ABAB5', background:'none', border:'none', cursor:txPage<=1?'not-allowed':'pointer', opacity:txPage<=1?0.35:1, fontFamily:"'Cairo',sans-serif" }}>السابق</button>
                <span style={{ fontSize:'0.72rem', color:'var(--wt-text-muted)' }}>{txPage} / {txPages}</span>
                <button disabled={txPage>=txPages||txLoading} onClick={() => loadTxs(txPage+1)} style={{ fontSize:'0.75rem', fontWeight:700, color:'#0ABAB5', background:'none', border:'none', cursor:txPage>=txPages?'not-allowed':'pointer', opacity:txPage>=txPages?0.35:1, fontFamily:"'Cairo',sans-serif" }}>التالي</button>
              </div>
            )}
          </div>
        )}

        {/* ── Requests ── */}
        {tab === 'requests' && (
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.65rem 1rem', borderBottom:'1px solid var(--wt-border)' }}>
              <span style={{ fontSize:'0.72rem', color:'var(--wt-text-muted)' }}>سجل طلبات الشحن</span>
              <button onClick={() => loadReqs(1)} style={{ fontSize:'0.72rem', fontWeight:700, color:'#0ABAB5', background:'none', border:'none', cursor:'pointer', fontFamily:"'Cairo',sans-serif" }}>تحديث</button>
            </div>
            {reqLoading ? (
              <div style={{ padding:'3rem 0', display:'flex', justifyContent:'center' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', border:'3px solid rgba(10,186,181,0.2)', borderTopColor:'#0ABAB5', animation:'wt-spin 0.7s linear infinite' }}/>
              </div>
            ) : reqs.length === 0 ? (
              <div style={{ padding:'3rem 1.5rem', textAlign:'center' }}>
                <div style={{ width:48, height:48, borderRadius:'0.875rem', background:'rgba(10,186,181,0.08)', border:'1px solid rgba(10,186,181,0.15)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 0.75rem' }}><FileText size={22} color="#0ABAB5" strokeWidth={1.8}/></div>
                <p style={{ color:'var(--wt-text-primary)', fontWeight:700, margin:'0 0 0.5rem', fontSize:'0.875rem' }}>لا توجد طلبات شحن</p>
                <button onClick={() => handleTabChange('topup')} style={{ fontSize:'0.75rem', fontWeight:800, color:'#0ABAB5', background:'none', border:'none', cursor:'pointer', fontFamily:"'Cairo',sans-serif" }}>أضف طلباً جديداً</button>
              </div>
            ) : (
              <div>
                {reqs.map((req, i) => {
                  const REQ_CFG = {
                    pending:    { label:'قيد المراجعة', color:'#f59e0b', bg:'rgba(245,158,11,0.1)',  border:'rgba(245,158,11,0.3)' },
                    approved:   { label:'تمت الموافقة', color:'#10b981', bg:'rgba(16,185,129,0.1)', border:'rgba(16,185,129,0.3)' },
                    rejected:   { label:'مرفوض',        color:'#f43f5e', bg:'rgba(244,63,94,0.1)',  border:'rgba(244,63,94,0.3)'  },
                    processing: { label:'جاري المعالجة',color:'#0ABAB5', bg:'rgba(10,186,181,0.1)', border:'rgba(10,186,181,0.3)' },
                    failed:     { label:'فشل',          color:'var(--wt-text-muted)', bg:'var(--wt-border)', border:'var(--wt-border)' },
                  };
                  const s = REQ_CFG[req.status] || REQ_CFG.pending;
                  const isCard = req.method === 'card';
                  return (
                    <div key={req.id} style={{
                      padding:'0.875rem 1rem',
                      borderBottom: i < reqs.length-1 ? '1px solid var(--wt-border)' : 'none',
                      animation:`wt-card-in 0.3s ease ${i*0.04}s both`,
                    }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.75rem', marginBottom: req.status==='rejected'&&req.reject_reason ? '0.65rem' : 0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
                          <div style={{ width:38, height:38, borderRadius:'0.75rem', flexShrink:0, background: isCard ? 'rgba(99,102,241,0.1)' : 'rgba(10,186,181,0.1)', border: `1px solid ${isCard?'rgba(99,102,241,0.2)':'rgba(10,186,181,0.2)'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem' }}>
                            {isCard ? '💳' : '🏦'}
                          </div>
                          <div>
                            <div style={{ color:'var(--wt-text-primary)', fontWeight:800, fontSize:'0.88rem', display:'flex', alignItems:'center', gap:'0.2rem', direction:'ltr' }}>
                              <SAR style={{ height:'0.85em', width:'auto' }}/>{fmt(req.amount)}
                            </div>
                            {req.bank_ref && <div style={{ color:'var(--wt-text-muted)', fontSize:'0.68rem' }}>#{req.bank_ref}</div>}
                            <div style={{ color:'var(--wt-text-muted)', fontSize:'0.65rem', opacity:0.7 }}>{fmtDate(req.created_at)}</div>
                          </div>
                        </div>
                        <span style={{ fontSize:'0.65rem', fontWeight:800, padding:'0.2rem 0.6rem', borderRadius:999, background:s.bg, border:`1px solid ${s.border}`, color:s.color, flexShrink:0, whiteSpace:'nowrap' }}>
                          {s.label}
                        </span>
                      </div>
                      {req.status==='rejected' && req.reject_reason && (
                        <div style={{ background:'rgba(244,63,94,0.08)', border:'1px solid rgba(244,63,94,0.2)', borderRadius:'0.625rem', padding:'0.5rem 0.75rem', fontSize:'0.7rem', color:'#f43f5e' }}>
                          <span style={{ fontWeight:800 }}>سبب الرفض: </span>{req.reject_reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {reqPages > 1 && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.75rem 1rem', borderTop:'1px solid var(--wt-border)' }}>
                <button disabled={reqPage<=1||reqLoading} onClick={() => loadReqs(reqPage-1)} style={{ fontSize:'0.75rem', fontWeight:700, color:'#0ABAB5', background:'none', border:'none', cursor:'pointer', fontFamily:"'Cairo',sans-serif", opacity:reqPage<=1?0.35:1 }}>السابق</button>
                <span style={{ fontSize:'0.72rem', color:'var(--wt-text-muted)' }}>{reqPage} / {reqPages}</span>
                <button disabled={reqPage>=reqPages||reqLoading} onClick={() => loadReqs(reqPage+1)} style={{ fontSize:'0.75rem', fontWeight:700, color:'#0ABAB5', background:'none', border:'none', cursor:'pointer', fontFamily:"'Cairo',sans-serif", opacity:reqPage>=reqPages?0.35:1 }}>التالي</button>
              </div>
            )}
          </div>
        )}

        {/* ── New Top-up ── */}
        {tab === 'topup' && (
          <div style={{ padding:'1.1rem' }}>

            {/* Method toggle */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'1rem' }}>
              {[
                { key:'card', label:'بطاقة / مدى', sub:'فوري ومباشر',  icon: paymentCardIcon, isImg:true },
                { key:'bank', label:'تحويل بنكي',  sub:'خلال 24 ساعة', icon:null, isImg:false },
              ].map(m => {
                const isAct = topupMethod === m.key;
                return (
                  <button key={m.key} onClick={() => setTopupMethod(m.key)} style={{
                    display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem',
                    padding:'0.875rem 0.5rem', borderRadius:'0.875rem', cursor:'pointer',
                    background: isAct ? 'rgba(10,186,181,0.1)' : 'var(--wt-surface)',
                    border: `1.5px solid ${isAct ? 'rgba(10,186,181,0.55)' : 'var(--wt-border)'}`,
                    boxShadow: isAct ? '0 0 14px rgba(10,186,181,0.15)' : 'none',
                    position:'relative', transition:'all 0.18s',
                  }}>
                    {isAct && (
                      <div style={{ position:'absolute', top:'0.4rem', left:'0.4rem', width:16, height:16, borderRadius:'50%', background:'#0ABAB5', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.55rem', color:'#fff', fontWeight:900 }}>✓</div>
                    )}
                    <div style={{ width:36, height:36, borderRadius:'0.625rem', background: isAct ? 'rgba(10,186,181,0.15)' : 'var(--wt-border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {m.isImg ? <img src={m.icon} alt="" style={{ width:20, height:20, filter: isAct?'none':'grayscale(0.6) opacity(0.6)' }}/> : <CreditCard size={18} color={isAct?'#0ABAB5':'var(--wt-text-muted)'} strokeWidth={1.8}/>}
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:'0.75rem', fontWeight:800, color: isAct ? '#0ABAB5' : 'var(--wt-text-primary)' }}>{m.label}</div>
                      <div style={{ fontSize:'0.62rem', color: isAct ? 'rgba(10,186,181,0.7)' : 'var(--wt-text-muted)', marginTop:'0.1rem' }}>{m.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Card payment */}
            {topupMethod === 'card' && (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
                  <PayLogo name="visa"/><PayLogo name="mc"/><PayLogo name="mada"/><PayLogo name="apple"/>
                </div>
                <button onClick={() => setCheckoutOpen(true)} style={{
                  width:'100%', padding:'0.875rem', borderRadius:'0.875rem', border:'none',
                  background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
                  fontSize:'0.875rem', fontWeight:800, cursor:'pointer',
                  boxShadow:'0 6px 20px rgba(10,186,181,0.35)', fontFamily:"'Cairo',sans-serif",
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  ادفع الآن — بطاقة · مدى · Apple Pay
                </button>
                <p style={{ textAlign:'center', fontSize:'0.65rem', color:'var(--wt-text-muted)', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  مدفوعات آمنة ومشفرة عبر Telr · PCI DSS Level 1
                </p>
              </div>
            )}

            {/* Bank transfer */}
            {topupMethod === 'bank' && (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
                {/* Bank details card */}
                <div style={{ borderRadius:'0.875rem', overflow:'hidden', border:'1px solid var(--wt-border)' }}>
                  <div style={{ padding:'0.6rem 0.875rem', background:'rgba(10,186,181,0.06)', borderBottom:'1px solid var(--wt-border)' }}>
                    <span style={{ fontSize:'0.72rem', fontWeight:800, color:'#0ABAB5' }}>بيانات الحساب البنكي</span>
                  </div>
                  {[
                    { label:'اسم البنك',   value: BANK_DETAILS.bankName },
                    { label:'اسم الحساب', value: BANK_DETAILS.accountName },
                    { label:'رقم الآيبان',value: BANK_DETAILS.iban },
                  ].map(({ label, value }, i, arr) => (
                    <div key={label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.6rem 0.875rem', background:'var(--wt-surface)', borderBottom: i<arr.length-1 ? '1px solid var(--wt-border)' : 'none' }}>
                      <span style={{ fontSize:'0.7rem', color:'var(--wt-text-muted)' }}>{label}</span>
                      <span style={{ fontSize:'0.7rem', fontWeight:800, color:'var(--wt-text-primary)' }} dir="ltr">{value}</span>
                    </div>
                  ))}
                  <div style={{ padding:'0.6rem 0.875rem', background:'rgba(245,158,11,0.07)', borderTop:'1px solid rgba(245,158,11,0.2)', display:'flex', gap:'0.5rem', alignItems:'flex-start' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" style={{ flexShrink:0, marginTop:2 }}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <span style={{ fontSize:'0.68rem', color:'rgba(245,158,11,0.85)' }}>{BANK_DETAILS.note}</span>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={submitTopup} style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                  {[
                    { label:'المبلغ المحوّل', key:'amount', type:'number', placeholder:'0.00', required:true, dir:'ltr' },
                    { label:'رقم الحوالة / الرقم المرجعي', key:'bank_ref', type:'text', placeholder:'TXN123456789', required:true, dir:'ltr' },
                    { label:'اسم البنك المحوّل منه', key:'bank_name', type:'text', placeholder:'بنك الراجحي', required:false, dir:'rtl' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color:'var(--wt-text-muted)', marginBottom:'0.35rem' }}>
                        {f.label} {f.required && <span style={{ color:'#f43f5e' }}>*</span>}
                      </label>
                      <input
                        className="wt-wallet-input"
                        type={f.type} placeholder={f.placeholder} required={f.required} dir={f.dir}
                        min={f.type==='number'?'1':undefined} step={f.type==='number'?'0.01':undefined}
                        value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                        style={inputStyle(false)}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, color:'var(--wt-text-muted)', marginBottom:'0.35rem' }}>ملاحظات <span style={{ opacity:0.5 }}>(اختياري)</span></label>
                    <textarea className="wt-wallet-input" rows={2} placeholder="أي معلومات إضافية..."
                      value={form.customer_notes} onChange={e => setForm(p => ({ ...p, customer_notes: e.target.value }))}
                      style={{ ...inputStyle(false), resize:'none' }}/>
                  </div>
                  <button type="submit" disabled={submitting} style={{
                    width:'100%', padding:'0.875rem', borderRadius:'0.875rem', border:'none',
                    background:'linear-gradient(135deg,#0ABAB5,#0891b2)', color:'#fff',
                    fontSize:'0.875rem', fontWeight:800, cursor: submitting?'not-allowed':'pointer',
                    opacity: submitting?0.7:1, boxShadow:'0 6px 20px rgba(10,186,181,0.3)',
                    fontFamily:"'Cairo',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
                  }}>
                    {submitting
                      ? <><span style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'wt-spin 0.7s linear infinite', display:'inline-block' }}/>جاري الإرسال...</>
                      : <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 13l4 4L19 7"/></svg>
                          إرسال طلب الشحن
                        </>
                    }
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CheckoutSheet */}
      <CheckoutSheet
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={(newBal) => {
          if (newBal !== undefined) setBalance(newBal);
          setCheckoutOpen(false);
          setTab('history');
          loadWallet();
        }}
      />
    </div>
  );
}

/* ── Icons ── */
function WalletIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 14a1 1 0 100-2 1 1 0 000 2z" />
    </svg>
  );
}
function RefreshIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}
function LockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}
function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}
function ListIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}
function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
    </svg>
  );
}
function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function PayLogo({ name }) {
  const wrap = "h-8 rounded-xl border border-gray-200 bg-white flex items-center justify-center overflow-hidden shadow-sm";

  if (name === 'visa') return (
    <div className={wrap} style={{ width: 56, padding: '4px 8px' }}>
      <img src={visaLogo} alt="Visa" style={{ height: 16, width: 'auto', objectFit: 'contain' }} />
    </div>
  );

  if (name === 'mc') return (
    <div className={wrap} style={{ width: 50, padding: '4px 8px' }}>
      <img src={mastercardLogo} alt="Mastercard" style={{ height: 24, width: 'auto', objectFit: 'contain' }} />
    </div>
  );

  if (name === 'mada') return (
    <div className={wrap} style={{ width: 60, padding: '4px 8px' }}>
      <img src={madaLogo} alt="mada" style={{ height: 22, width: 'auto', objectFit: 'contain' }} />
    </div>
  );

  if (name === 'apple') return (
    <div className={wrap} style={{ width: 72, padding: '4px 8px' }}>
      <img src={applePayLogo} alt="Apple Pay" style={{ height: 22, width: 'auto', objectFit: 'contain' }} />
    </div>
  );

  return null;
}
